<script setup>
import { computed, onBeforeUnmount, ref, toRef, watch } from 'vue'
import { useAppearanceStore } from '@stores/appearance'
import { useAuthStore } from '@stores/auth'
import { useChannelsStore } from '@stores/channels'
import { useNowPlayingStore } from '@stores/now-playing'
import { usePlaybackStore } from '@stores/playback'
import { useQueueService } from '@composables/use-queue-service'
import { useDismissable } from '@composables/use-dismissable'
import modalService from '@/services/modal-service'
import { STATION_ORDER, STREAMING_STATUSES } from '@data/stations'

const props = defineProps({
    open: {
        type: Boolean,
        default: false,
    },
    // The launcher button — a pointerdown on it must not count as "outside".
    // No `type`: an HTMLElement fails Vue's `Object` prop check.
    anchor: {
        type: null,
        default: null,
    },
})

const emit = defineEmits(['close'])

const appearanceStore = useAppearanceStore()
const authStore = useAuthStore()
const channelsStore = useChannelsStore()
const nowPlayingStore = useNowPlayingStore()
const playbackStore = usePlaybackStore()
const queueService = useQueueService()
const panel = ref(null)

const stations = computed(() =>
    channelsStore.channels
        .filter(channel => STATION_ORDER.includes(channel.id))
        .sort((a, b) => STATION_ORDER.indexOf(a.id) - STATION_ORDER.indexOf(b.id))
)

const playingStationId = computed(() => {
    const resource = playbackStore.resource
    return resource && resource.type === 'channel' && STREAMING_STATUSES.includes(playbackStore.status)
        ? resource.id
        : null
})

function currentMix(station) {
    const current = nowPlayingStore.currentForStation(station.id)
    return (current && current.mixes) || null
}

function nowPlayingLine(station) {
    const mix = currentMix(station)
    if (!mix) return '—'
    return [mix.artist && mix.artist.title, mix.show && mix.show.title].filter(Boolean).join(' · ') || '—'
}

// The episode carries the artwork; without a url the row shows the radio placeholder.
function artwork(station) {
    const mix = currentMix(station)
    const episode = mix && mix.episode
    return episode && episode.album_art && episode.album_art.url ? episode : null
}

// `open` is the launcher's state, so it only flips after the launcher re-renders. Escape
// reaches both the capture listener below and useDismissable's own — emit once per open.
let hasRequestedClose = false

watch(() => props.open, open => {
    if (open) hasRequestedClose = false
})

// Focus goes back to the launcher ONLY for Escape and row activation — never for an
// outside pointerdown or a route change (same ruling as _base-context-menu.vue).
function requestClose({ restoreFocus }) {
    if (hasRequestedClose) return
    hasRequestedClose = true
    emit('close', { restoreFocus })
}

function onRowClick(station) {
    if (playingStationId.value === station.id) {
        queueService.pause()
    } else if (!authStore.loggedIn) {
        // D2: the same gate every other radio play control has today.
        modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
    } else {
        Promise.resolve(queueService.playRadioChannel(station.id)).catch(e => {
            appearanceStore.handleNotification({ message: e.message, type: 'warning' })
        })
    }
    requestClose({ restoreFocus: true })
}

// useDismissable's single onDismiss can't tell Escape from an outside pointerdown, so
// Escape is caught first here, in the capture phase, to ask for focus back (the
// _base-context-menu.vue recipe).
function onEscapeCapture(event) {
    if (event.key === 'Escape') requestClose({ restoreFocus: true })
}

function detachEscape() {
    if (typeof document === 'undefined') return
    document.removeEventListener('keydown', onEscapeCapture, true)
}

watch(() => props.open, open => {
    if (typeof document === 'undefined') return
    if (open) {
        document.addEventListener('keydown', onEscapeCapture, true)
    } else {
        detachEscape()
    }
}, { immediate: true })

onBeforeUnmount(detachEscape)

useDismissable({
    isOpen: toRef(props, 'open'),
    onDismiss: () => requestClose({ restoreFocus: false }),
    inside: () => [panel.value, props.anchor],
})
</script>

<template>
    <transition name="settle">
        <div
            v-if="open"
            ref="panel"
            class="station-menu"
            role="menu"
            aria-label="Live radio"
        >
            <button
                v-for="station in stations"
                :key="station.id"
                type="button"
                :class="['station-row', { 'station-row-playing': playingStationId === station.id }]"
                role="menuitem"
                @click="onRowClick(station)"
            >
                <span
                    class="art"
                    aria-hidden="true"
                >
                    <BaseResourceImage
                        v-if="artwork(station)"
                        class="art-image"
                        :resource="artwork(station)"
                        type="album_art"
                        :should-lazyload="false"
                        radius="list"
                    />
                    <BaseIcon
                        v-else
                        name="radio"
                        :size="20"
                    />
                </span>
                <span class="text">
                    <span class="station-name">{{ station.title }}</span>
                    <span class="now-playing">{{ nowPlayingLine(station) }}</span>
                </span>
                <BaseIcon
                    class="glyph"
                    :name="playingStationId === station.id ? 'pause' : 'play'"
                    :size="20"
                />
            </button>
        </div>
    </transition>
</template>

<style lang="scss" scoped>
@use '@design' as *;

$menu-width: 380px;
$row-height: 56px;
$art: 40px;

.station-menu {
    position: absolute;
    top: calc(100% + #{$space-8});
    // D27: anchor to the pill's OUTER right edge (the wrap sits inside the pill's 8px right padding).
    right: -$space-8;
    z-index: $layer-message-z-index;
    width: $menu-width;
    padding: $space-8;
    border-radius: $radius-12;

    @include elevationOverlay;
}

.station-row {
    display: flex;
    gap: $space-12;
    align-items: center;
    width: 100%;
    height: $row-height;
    padding: 0 $space-8;
    color: $c-text-primary;
    text-align: left;
    cursor: pointer;
    background: transparent;
    border: 0;
    border-radius: $radius-8;

    @include transition(background-color, fast);
    @include focusRing;

    // Tablets render this menu too — keep the highlight off after a tap.
    @media (hover: hover) {
        &:hover {
            background: $c-state-hover;

            .glyph {
                color: $c-text-primary;
            }
        }
    }
}

.art {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    color: $c-text-secondary;
    background: $c-surface-raised;
    border-radius: $radius-3;

    @include square($art);
}

.art-image {
    width: 100%;
    height: 100%;
}

.text {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
}

.station-name {
    @include bodySmallEmphasis;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.now-playing {
    @include secondary;

    overflow: hidden;
    text-overflow: ellipsis;
    color: $c-text-tertiary;
    white-space: nowrap;
}

.glyph {
    flex-shrink: 0;
    color: $c-text-tertiary;

    @include transition(color, fast);
}

.station-row-playing {
    .station-name,
    .glyph {
        color: $c-accent-default;
    }

    @media (hover: hover) {
        &:hover .glyph {
            color: $c-accent-default;
        }
    }
}

// Fade + 4px settle at base/enter; close at fast/exit.
.settle-enter-active {
    @include transition((opacity, transform), base, enter);
}

.settle-leave-active {
    @include transition(opacity, fast, exit);
}

.settle-enter-from {
    opacity: 0;
    transform: translateY(-#{$space-4});
}

.settle-leave-to {
    opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
    .settle-enter-from {
        transform: none;
    }
}
</style>
